import React, { useCallback, useEffect, useState } from 'react';
import { RadioTower, Unlock, Settings, Trash2 } from 'lucide-react';

import gateSystemsService from '../services/gateSystemsService';
import { formatDateTime } from '../utils/datetime';

/**
 * Remote opener — a relay board wired IN PARALLEL with the facility's gate
 * system.
 *
 * It sits in its own slot on purpose. The gate controller above decides what
 * a tenant is sent at move-in; this only opens the gate and reports on the
 * board, and connecting it changes nothing about codes or access. That is
 * what makes it safe to add at a facility that already has tenants.
 *
 * - A stored key is never shown again; a blank key field means "keep it".
 * - Board status is the LAST check with its time — never an assumed "online".
 * - Opening moves a real gate once the relay is landed, so it asks first.
 */
const Pill = ({ tone, children }) => {
  const tones = {
    ok: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    bad: 'bg-red-50 text-red-700 ring-red-200',
    idle: 'bg-slate-50 text-slate-600 ring-slate-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
};

const inputCls = 'mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]';

const RemoteOpenerCard = ({ facilityId, timeZone }) => {
  const [view, setView] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ baseUrl: '', latchId: '', apiKey: '', allowLive: false });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await gateSystemsService.getRemoteOpener(facilityId);
      setView(data);
      setForm({
        baseUrl: data?.base_url || '',
        latchId: data?.latch_id || '',
        apiKey: '',
        allowLive: !!data?.allow_live,
      });
    } catch {
      setView(null);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const fail = (e, fallback) =>
    setMsg({ ok: false, text: e?.response?.data?.detail || fallback });

  const save = async () => {
    setBusy('save');
    setMsg(null);
    try {
      await gateSystemsService.saveRemoteOpener(facilityId, {
        apiKey: form.apiKey.trim(),
        baseUrl: form.baseUrl.trim(),
        latchId: form.latchId.trim(),
        allowLive: form.allowLive,
      });
      setEditing(false);
      await load();
      setMsg({ ok: true, text: 'Saved. Run a check to confirm the board is online.' });
    } catch (e) {
      fail(e, 'Could not save the opener.');
    } finally {
      setBusy('');
    }
  };

  const check = async () => {
    setBusy('check');
    setMsg(null);
    try {
      await gateSystemsService.checkRemoteOpener(facilityId);
      await load();
    } catch (e) {
      fail(e, 'Could not check the opener.');
    } finally {
      setBusy('');
    }
  };

  const open = async () => {
    if (!window.confirm(
      'Open the gate now?\n\nIf the relay is landed on the operator, the gate '
      + 'will move — make sure the area is clear.')) return;
    setBusy('open');
    setMsg(null);
    try {
      const { data } = await gateSystemsService.openGate(facilityId);
      setMsg({ ok: true, text: `Relay fired ${formatDateTime(data.opened_at, timeZone)}` });
    } catch (e) {
      fail(e, 'Could not open the gate.');
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    if (!window.confirm('Disconnect the remote opener? The gate controller and '
      + 'tenant access are not affected.')) return;
    setBusy('remove');
    setMsg(null);
    try {
      await gateSystemsService.removeRemoteOpener(facilityId);
      setEditing(false);
      await load();
    } catch (e) {
      fail(e, 'Could not disconnect the opener.');
    } finally {
      setBusy('');
    }
  };

  const configured = !!view?.configured;
  const last = view?.last_check;
  const boardTone = !last ? 'idle' : last.board_online === true ? 'ok'
    : last.board_online === false ? 'bad' : 'idle';
  const boardText = !last ? 'Not checked yet'
    : last.board_online === true ? 'Board online'
      : last.board_online === false ? 'Board offline'
        : last.reachable ? 'Board not found' : 'Unreachable';

  return (
    <section
      className="rounded-xl border border-slate-200/80 bg-white overflow-hidden"
      data-testid="remote-opener-card"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <RadioTower className="h-4 w-4 text-slate-500" />
          <div>
            <h3 className="text-[13px] font-semibold text-slate-900">Remote opener</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              A relay board wired alongside your gate system. It opens the gate
              on demand and changes nothing about codes or tenant access.
            </p>
          </div>
        </div>
      </header>
      <div className="p-4">
        {configured && !editing && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium text-slate-900">
                  {last?.board_name || 'Relay board'}
                </span>
                <Pill tone={boardTone}>{boardText}</Pill>
                {view.test_key && <Pill tone="idle">Test key — opens are simulated</Pill>}
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {last?.checked_at
                  ? <>Checked {formatDateTime(last.checked_at, timeZone)}</>
                  : 'Run a check to see whether the board is online.'}
                {last?.message && last.board_online !== true && <> &middot; {last.message}</>}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={open}
                disabled={!!busy}
                data-testid="remote-opener-open"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[12px] font-medium whitespace-nowrap"
              >
                <Unlock className="h-3.5 w-3.5" />
                {busy === 'open' ? 'Opening…' : 'Open gate'}
              </button>
              <button
                type="button"
                onClick={check}
                disabled={!!busy}
                data-testid="remote-opener-check"
                className="px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded"
              >
                {busy === 'check' ? 'Checking…' : 'Check'}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(true); setMsg(null); }}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded"
              >
                <Settings className="h-3 w-3" /> Configure
              </button>
            </div>
          </div>
        )}

        {!configured && !editing && (
          <div className="text-center py-5">
            <p className="text-[12px] text-slate-500 mb-3">
              No remote opener connected for this facility.
            </p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="remote-opener-connect"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
            >
              Connect an opener
            </button>
          </div>
        )}

        {editing && (
          <div className="space-y-3 max-w-xl">
            <label className="block">
              <span className="text-[12px] font-medium text-slate-700">Service address</span>
              <input
                className={inputCls}
                value={form.baseUrl}
                placeholder="https://… (leave blank for the provider's default)"
                data-testid="remote-opener-url"
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-slate-700">Board (latch) id</span>
              <input
                className={inputCls}
                value={form.latchId}
                data-testid="remote-opener-latch"
                onChange={(e) => setForm({ ...form, latchId: e.target.value })}
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                From your provider&rsquo;s portal. It names the one board this facility opens.
              </span>
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-slate-700">Key</span>
              <input
                type="password"
                className={inputCls}
                value={form.apiKey}
                data-testid="remote-opener-key"
                placeholder={view?.has_api_key
                  ? '•••• stored — leave blank to keep'
                  : 'Paste the key your provider issued'}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                Stored encrypted and never shown again.
              </span>
            </label>
            <label className="flex items-start gap-2 text-[12px] text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.allowLive}
                data-testid="remote-opener-live"
                onChange={(e) => setForm({ ...form, allowLive: e.target.checked })}
              />
              <span>
                Accept a live key. A live key fires the real relay; a test key
                only simulates an open.
              </span>
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={save}
                disabled={!!busy || !form.latchId.trim()}
                data-testid="remote-opener-save"
                className="px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[13px] font-medium"
              >
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setMsg(null); load(); }}
                className="px-3.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium"
              >
                Cancel
              </button>
              {configured && (
                <button
                  type="button"
                  onClick={remove}
                  disabled={!!busy}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] text-red-700 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-3 w-3" /> Disconnect
                </button>
              )}
            </div>
          </div>
        )}

        {msg && (
          <p
            className={`mt-2 text-[11px] ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}
            data-testid="remote-opener-msg"
          >
            {msg.text}
          </p>
        )}
      </div>
    </section>
  );
};

export default RemoteOpenerCard;
